import {useEditMasters,masterOptions,isMasterField,applyEditContainer,EditMasterPicker} from "@/components/EditMasterPicker";
import React, {useEffect,useState} from "react";
import {useNavigate,useParams,Link} from "react-router-dom";
import {api} from "@/lib/api";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {toast} from "sonner";
import {useAuth} from "@/auth";

const FIELDS=[
 ["customer_name","Customer Name *"],["customer_address","Customer Address"],
 ["contact","Contact Person / Phone"],["container_type","Type of Container *"],
 ["external_size","External Size (mm)"],["internal_size","Internal Size (mm)"],
 ["inside_wall","Inside Finish — Walls"],["inside_roof","Inside Finish — Roof"],
 ["outside_finish","Outside Finish"],["base","Base"],["rear_door","Rear Door"],
 ["side_door","Side Door"],["hatch_door","Hatch Door"],
 ["insulation","Insulation Thickness"],["floor","Floor"],
 ["delivery_time","Delivery Time"],["vehicle_model","Vehicle Model"],
 ["reefer","Reefer Unit / Scope"],["price_details","Price Details"],
 ["payment_details","Payment Details"],["special_customization","Special Customization"]
];
const LONG=new Set(["customer_address","reefer","price_details","payment_details","special_customization"]);

export default function PCRecordEditPage(){
 const {pcId}=useParams();
 const {user}=useAuth();
 const nav=useNavigate();
 const [pc,setPc]=useState(null);
 const [form,setForm]=useState({});
 const [saving,setSaving]=useState(false);
 const [error,setError]=useState("");
 const masters=useEditMasters();
 const updateField=(k,v)=>setForm(f=>({...f,[k]:v}));
 const chooseMaster=(k,x)=>{if(k==="container_type")setForm(f=>applyEditContainer(f,x));else if(k==="special_customization")updateField(k,[form[k],x.name||x.model||x.title].filter(Boolean).join("\n"));else updateField(k,typeof x==="string"?x:(x.name||x.model||x.title||""));};
 useEffect(()=>{
  let active=true;
  api.get("/record-edit/pc/"+encodeURIComponent(pcId)).then(r=>{
   if(!active)return;
   setPc(r.data.pc);
   setForm(Object.fromEntries(FIELDS.map(([key])=>[key,r.data.pc[key]||""])));
  }).catch(e=>{if(active)setError(e.response?.data?.detail||"Unable to open PC for editing")});
  return()=>{active=false};
 },[pcId]);
 const save=async()=>{
  if(!String(form.customer_name||"").trim())return toast.error("Customer name is required");
  if(pc?.format_version===2&&!String(form.container_type||"").trim())return toast.error("Container type is required");
  if(!window.confirm("Save edits to this existing PC? The PC number, original quotation and chassis link will remain unchanged."))return;
  setSaving(true);
  try{
   const payload=Object.fromEntries(FIELDS.map(([key])=>[key,form[key]||""]));
   await api.put("/record-edit/pc/"+encodeURIComponent(pcId),payload);
   toast.success("PC updated without issuing a new number");
   nav(user?.role==="assistant"?"/records":"/pcs");
  }catch(e){toast.error(e.response?.data?.detail||"PC update failed")}
  finally{setSaving(false)}
 };
 if(error)return <div className="fc-card p-5"><p className="text-rose-600">{error}</p><Link to="/pcs" className="text-blue-600 text-sm">Back to All PCs</Link></div>;
 if(!pc)return <div className="p-8 text-slate-500">Loading existing PC…</div>;
 return <div className="max-w-3xl mx-auto pb-20 space-y-4">
  <div><Link to="/pcs" className="text-blue-600 text-sm">← All PCs</Link><h1 className="text-2xl font-semibold mt-2">Edit PC Details</h1><p className="text-sm text-slate-500 mt-1">Editing the existing PC, not creating a replacement.</p></div>
  <div className="fc-card p-4 space-y-2">
   <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">PC identity · locked</div>
   <div className="font-semibold">{pc.pc_reference||pc.pc_number}</div>
   <div className="text-sm text-slate-600">Type: {pc.pc_type||"Legacy"} · Date: {pc.pc_date||"—"} · Quantity: {pc.quantity||1}</div>
   <div className="text-xs text-slate-500">Serial, financial year, quotation reference, quantity, confirmed prices and chassis attachment cannot be changed here.</div>
  </div>
  {masters.loadError&&<p className="text-xs text-amber-700">{masters.loadError}</p>}
  <div className="fc-card p-4 space-y-4">
   {FIELDS.map(([key,label])=><div key={key}>
    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
    {isMasterField(key)
      ? <EditMasterPicker label={label} value={form[key]||""} options={masterOptions(key,masters)} multiline={LONG.has(key)} onChange={v=>updateField(key,v)} onPick={x=>chooseMaster(key,x)}/>
      : LONG.has(key)
      ? <Textarea value={form[key]||""} onChange={e=>updateField(key,e.target.value)} rows={3} className="bg-white"/>
      : <Input value={form[key]||""} onChange={e=>updateField(key,e.target.value)} className="h-11 bg-white"/>}
   </div>)}
  </div>
  <div className="grid grid-cols-2 gap-2">
   <Link to="/pcs" className="h-11 rounded-xl border border-slate-300 flex items-center justify-center text-sm">Cancel</Link>
   <button type="button" disabled={saving} onClick={save} className="h-11 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-50">{saving?"Saving…":"Save PC Changes"}</button>
  </div>
 </div>;
}
